import { Grid, makeStyles } from "@material-ui/core";
import { lang } from "i18nLang";
import { useEffect, useState } from "react";
import { formatNumber, formatNumberToMonetary } from "utils";
import {
  BillingDetailValues,
  BillingDetailsRow,
  DetailTitle,
  DetailValue,
} from "./DetailsTableStickers";
import { PeriodLink } from "./TableStickers";

const label = "measurements.table";

const useStyles = makeStyles((theme) => ({
  cell: {
    minHeight: "32px",
    width: "100%",
    padding: "0 16px",
    border: "none",
    lineHeight: "1rem",
    fontSize: "0.875rem",
    textAlign: "left",
    fontWeight: 400,
    letterSpacing: "0.01071em",
  },
  cellHead: {
    height: "2rem !important",
    color: "rgba(0, 0, 0, 0.87)",
    fontWeight: 500,
  },
  detailTitle: {
    color: theme.palette.gray.dark,
    fontSize: ".563rem",
    fontWeight: "bold",
  },
  detailContainer: {
    marginBottom: theme.spacing(2),
    paddingTop: "0 !important",
  },
  detailLabel: { color: theme.palette.gray[600], fontSize: "0.563rem" },
  detailValue: { fontSize: "0.75rem" },
  groupBox: {
    maxWidth: "fit-content",
    height: "fit-content",
    border: "1px solid #BDBDBD",
    borderRadius: 4,
    marginTop: 15,

    "& .title": {
      height: "fit-content !important",
      position: "relative",
      minHeight: "fit-content",
      top: "-7px",
      "& > span": {
        background: "#EEEEEE",
        padding: "0 0.25rem",
        marginLeft: "-0.25rem",
      },
    },
    "& .container": {
      width: "fit-content",
    },
    "& .billing-details": {
      marginTop: 0,
      minWidth: 230,
      "& > div": {
        width: "fit-content",
      },
    },
    "& .left > div": {
      paddingRight: 8,
      width: "fit-content",
    },
    "& .right": {
      maxWidth: "fit-content",
      "& > div": {
        paddingLeft: 8,
      },
    },
  },
}));

export const DetailsTable = ({
  row,
  onChangePeriod,
}: {
  row: any;
  onChangePeriod: (uid: string, initialDate: Date, finalDate: Date) => void;
}) => {
  const classes = useStyles();
  const [sizes, setSizes] = useState<number[]>([]);

  useEffect(() => {
    handleDivResize();
  }, []);

  const handleChangePeriod = (date) => {
    onChangePeriod(row.uid, date.start, date.end);
  };

  function handleDivResize() {
    let newSizes: number[] = [];
    const header = document.querySelector(".measurements-table-header");

    if (header) {
      newSizes = Array.from(header.children).map(
        (col) => (col as HTMLElement).offsetWidth
      );
    }

    setSizes(newSizes);
  }

  return (
    <Grid
      container
      align-items="flex-start"
      style={{ paddingBottom: "1rem" }}
      // onResize={handleDivResize}
    >
      <Grid item style={{ width: sizes[0] }} />
      <Grid item style={{ width: sizes[1] }}>
        <DetailTitle
          classes={classes}
          label={lang(`${label}.measurementInformation.title`)}
        />
        <DetailValue
          classes={classes}
          label={lang(`${label}.measurementInformation.measuredValue`)}
          value={formatNumberToMonetary(row.totalMeasured)}
        />
        <DetailValue
          classes={classes}
          label={lang(`${label}.measurementInformation.readjustment`)}
          value={formatNumberToMonetary(row.readjustValue)}
          show={Boolean(row.readjustValue)}
        />
      </Grid>
      <Grid item style={{ width: sizes[2] }}>
        <DetailTitle
          classes={classes}
          label={lang(`${label}.discounts.title`)}
          show={Boolean(row.billingsTotal) || Boolean(row.discountsTotal)}
        />
        <DetailValue
          classes={classes}
          label={lang(`${label}.discounts.directBilling`)}
          value={formatNumberToMonetary(row.billingsTotal)}
          show={Boolean(row.billingsTotal)}
        />
        <DetailValue
          classes={classes}
          label={lang(`${label}.discounts.totalExtraordinaryDiscount`)}
          value={formatNumberToMonetary(row.discountsTotal)}
          show={Boolean(row.discountsTotal)}
        />
      </Grid>
      <Grid item style={{ width: sizes[3] }} />
      <Grid item style={{ width: sizes[4] }}>
        <DetailTitle
          classes={classes}
          label={lang(`${label}.retentionAndAdvances.title`)}
          show={Boolean(row.retentionTotal) || Boolean(row.advancesTotal)}
        />
        <DetailValue
          classes={classes}
          label={`${lang(
            `${label}.retentionAndAdvances.retention`
          )} - ${formatNumber(row.technicalRetentionPercent)}%`}
          value={formatNumberToMonetary(row.retentionTotal)}
          show={Boolean(row.retentionTotal)}
        />
        <DetailValue
          classes={classes}
          label={lang(`${label}.retentionAndAdvances.advanceDiscount`)}
          value={formatNumberToMonetary(row.advancesTotal)}
          show={Boolean(row.advancesTotal)}
        />
      </Grid>
      <Grid item style={{ width: sizes[5] }}>
        <DetailTitle
          classes={classes}
          label={lang(`${label}.taxes.title`)}
          show={
            Boolean(row.issPercent) ||
            Boolean(row.inssPercent) ||
            Boolean(row.pisPercent) ||
            Boolean(row.cofinsPercent) ||
            Boolean(row.csllPercent)
          }
        />
        <DetailValue
          classes={classes}
          label={`${lang(`${label}.taxes.iss`)} - ${formatNumber(
            row.issPercent
          )}%`}
          value={formatNumberToMonetary(row.taxesValues.issValue)}
          show={Boolean(row.issPercent)}
        />
        <DetailValue
          classes={classes}
          label={`${lang(`${label}.taxes.inss`)} - ${formatNumber(
            row.inssPercent
          )}%`}
          value={formatNumberToMonetary(row.taxesValues.inssValue)}
          show={Boolean(row.inssPercent)}
        />
        <DetailValue
          classes={classes}
          label={`${lang(`${label}.taxes.pis`)} - ${formatNumber(
            row.pisPercent
          )}%`}
          value={formatNumberToMonetary(row.taxesValues.pisValue)}
          show={Boolean(row.pisPercent)}
        />
        <DetailValue
          classes={classes}
          label={`${lang(`${label}.taxes.cofins`)} - ${formatNumber(
            row.cofinsPercent
          )}%`}
          value={formatNumberToMonetary(row.taxesValues.cofinsValue)}
          show={Boolean(row.cofinsPercent)}
        />
        <DetailValue
          classes={classes}
          label={`${lang(`${label}.taxes.csll`)} - ${formatNumber(
            row.csllPercent
          )}%`}
          value={formatNumberToMonetary(row.taxesValues.csllValue)}
          show={Boolean(row.csllPercent)}
        />
      </Grid>
      <Grid item xs className={classes.groupBox}>
        <DetailTitle
          classes={classes}
          label={lang(`${label}.otherInformation.title`)}
        />
        <Grid container direction="row" className="container">
          <Grid item className="left" style={{ maxWidth: sizes[6] + 60 }}>
            <BillingDetailsRow
              classes={classes}
              invoiceNumber={row.billingOrder?.invoiceNumberCustom}
              invoiceSerial={row.billingOrder?.invoiceSerial}
              invoiceDate={row.billingOrder?.invoiceDate}
            />
            <BillingDetailValues
              classes={classes}
              label={lang(`${label}.orderCode`)}
              value={row.billingOrder?.orderCodeCustom || "-"}
              caption={row.billingOrder?.message}
              error={row.billingOrder?.error}
            />
            <DetailValue
              classes={classes}
              label={lang(`${label}.otherInformation.measuredPercent`)}
              value={`${formatNumber(row.measuredPercentual)}%`}
            />
          </Grid>
          <Grid item xs className="right">
            <DetailValue
              classes={classes}
              label={lang("fields.period")}
              value={
                <PeriodLink
                  id={row.uid}
                  initialDate={row.initialDate}
                  finalDate={row.finalDate}
                  onChange={handleChangePeriod}
                />
              }
            />
            <DetailValue
              classes={classes}
              label={lang(`${label}.otherInformation.balanceToMeasure`)}
              value={formatNumberToMonetary(row.balanceToMeasure)}
            />
            <DetailValue
              classes={classes}
              label={lang(`${label}.otherInformation.accumulatedPercentage`)}
              value={`${formatNumber(row.accumulatedPercentual)}%`}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
